// src/admin/LenderManagementPage.jsx
import React from "react";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit2,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
  KeyRound,
  Mail,
  Building2,
  User,
  Lock,
  ChevronDown,
  AlertCircle,
  Copy,
  Download,
  FileText,
  RefreshCw,
} from "lucide-react";
import { TableSkeleton } from "../../components/ui/Skeleton.jsx";
import { adminListUsers, adminDeleteUser } from "../../api/admin/adminListUsers.js";

/**
 * Lender Management: list from API (dynesourcerapi.user_directory.admin_list_users).
 * Table: Lender ID, Name, Email, Status, Actions.
 * Search is sent to the API; filter by status is client-side.
 */

/** Map API user/lender record to table row shape (message.data[] from admin_list_users) */
function mapLenderRow(row) {
  const firstLast = [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || null;
  const name = row.full_name ?? firstLast ?? row.name ?? row.email ?? "—";
  const email = row.email ?? row.name ?? "";
  const enabled = row.enabled !== false && row.enabled !== 0;
  return {
    id: row.name ?? row.email ?? String(row.id ?? ""),
    name,
    products: row.products ?? row.product_count ?? 0,
    email,
    phone: row.mobile_no ?? row.phone ?? "",
    website: row.website ?? "",
    address: row.address ?? "",
    region: row.region ?? "",
    status: enabled ? "Active" : "Inactive",
    tone: enabled ? "blue" : "red",
    username: row.name ?? row.email ?? "",
    type: row.type ?? "",
    interestRate: row.interest_rate ?? "",
    maxLoanAmount: row.max_loan_amount ?? "",
    minCreditScore: row.min_credit_score ?? "",
    productsOffered: row.products_offered ?? [],
    processingTime: row.processing_time ?? "",
    approvalRate: row.approval_rate ?? "",
    totalLoans: row.total_loans ?? "",
    establishedDate: row.established_date ?? "",
    contactPerson: row.contact_person ?? "",
    notes: row.notes ?? "",
  };
}

function StatusPill({ status, tone }) {
  const base = "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium";
  if (tone === "red") {
    return (
      <span className={`${base} bg-red-100 text-red-700`}>
        <XCircle className="h-4 w-4" />
        {status}
      </span>
    );
  }
  return (
    <span className={`${base} bg-sky-100 text-sky-700`}>
      <CheckCircle2 className="h-4 w-4" />
      {status}
    </span>
  );
}

function Modal({ open, title, onClose, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto flex-1">{children}</div>
          <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center justify-between">
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-medium text-slate-600">{label}</div>
      <div className="mt-1 relative">
        {Icon ? <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /> : null}
        <div className={Icon ? "pl-9" : ""}>{children}</div>
      </div>
    </label>
  );
}

function Input({ value, onChange, placeholder, type = "text", disabled = false, step }) {
  return (
    <input
      type={type}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      step={step}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-500"
    />
  );
}

function Textarea({ value, onChange, placeholder, disabled = false, rows = 3 }) {
  return (
    <textarea
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-500"
    />
  );
}

function Select({ value, onChange, options, disabled = false }) {
  return (
    <div className="relative">
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2 pr-9 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50 disabled:text-slate-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
    </div>
  );
}

function makeId(prefix = "LND") {
  const n = Math.floor(100000000 + Math.random() * 900000000);
  return `${prefix}-${n}`;
}

function slugifyUsername(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 24);
}

function genPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  let out = "";
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function LenderManagementPage() {
  const [lenders, setLenders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("All");
  const [modal, setModal] = React.useState({ open: false, mode: "add", lender: null });
  const [draft, setDraft] = React.useState(null);
  const [flashPassword, setFlashPassword] = React.useState("");
  const [notification, setNotification] = React.useState(null);

  const fetchLenders = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminListUsers({
        limit_start: 0,
        limit_page_length: 100,
        role: "Lender",
        exclude_system_manager: 1,
        search: q.trim() || undefined,
      });
      setLenders((Array.isArray(data) ? data : []).map(mapLenderRow));
    } catch (err) {
      console.error("Failed to fetch lenders:", err);
      setLenders([]);
      setNotification({ message: "Failed to load lenders.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [q]);

  React.useEffect(() => {
    fetchLenders();
  }, [fetchLenders]);

  const debouncedSearch = React.useRef(null);
  React.useEffect(() => {
    if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
    debouncedSearch.current = setTimeout(() => setQ(searchInput), 300);
    return () => {
      if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
    };
  }, [searchInput]);

  const filtered = React.useMemo(() => {
    return lenders.filter((l) => statusFilter === "All" || l.status === statusFilter);
  }, [lenders, statusFilter]);

  // Notification Handler
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const openAdd = () => {
    const pwd = genPassword();
    const base = {
      id: makeId("LND"),
      name: "",
      email: "",
      username: "",
      password: pwd,
      status: "Active",
      tone: "blue",
      products: 0,
    };
    setFlashPassword(pwd);
    setDraft(base);
    setModal({ open: true, mode: "add", lender: null });
  };

  const openEdit = (l) => {
    setFlashPassword("");
    setDraft({ ...l, password: "" });
    setModal({ open: true, mode: "edit", lender: l });
  };

  const openView = (l) => {
    setFlashPassword("");
    setDraft({ ...l, password: "" });
    setModal({ open: true, mode: "view", lender: l });
  };

  const closeModal = () => {
    setModal({ open: false, mode: "add", lender: null });
    setDraft(null);
    setFlashPassword("");
  };

  const saveAdd = () => {
    if (!draft?.name?.trim()) {
      showNotification("Lender name is required", "error");
      return;
    }
    if (!draft?.email?.trim()) {
      showNotification("Email is required", "error");
      return;
    }
    if (!draft?.username?.trim()) {
      showNotification("Username is required", "error");
      return;
    }
    if (!draft?.password?.trim()) {
      showNotification("Password is required", "error");
      return;
    }

    setLenders((prev) => [{ ...draft }, ...prev]);
    showNotification("Lender added successfully!");
    closeModal();
  };

  const saveEdit = () => {
    if (!draft?.name?.trim()) {
      showNotification("Lender name is required", "error");
      return;
    }
    if (!draft?.email?.trim()) {
      showNotification("Email is required", "error");
      return;
    }
    if (!draft?.username?.trim()) {
      showNotification("Username is required", "error");
      return;
    }

    setLenders((prev) => prev.map((x) => (x.id === draft.id ? { ...x, ...draft, password: undefined } : x)));
    showNotification("Lender updated successfully!");
    closeModal();
  };

  const deleteLender = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lender? This cannot be undone.")) return;
    try {
      const result = await adminDeleteUser(id);
      if (result.ok) {
        setLenders((prev) => prev.filter((l) => l.id !== id));
        showNotification("Lender deleted successfully!");
      } else {
        showNotification(result.error || "Failed to delete lender", "error");
      }
    } catch {
      showNotification("Failed to delete lender", "error");
    }
  };

  const resetPassword = () => {
    const pwd = genPassword();
    setDraft((d) => ({ ...d, password: pwd }));
    setFlashPassword(pwd);
    showNotification("Password reset successfully!");
  };

  const setStatus = (status) => {
    const tone = status === "Active" ? "blue" : "red";
    setDraft((d) => ({ ...d, status, tone }));
  };

  const handleExport = () => {
    const csv = [
      [
        "Lender ID",
        "Name",
        "Type",
        "Email",
        "Phone",
        "Status",
        "Interest Rate",
        "Max Loan",
        "Region",
      ],
      ...filtered.map((l) => [
        l.id,
        l.name,
        l.type || "",
        l.email,
        l.phone || "",
        l.status,
        l.interestRate || "",
        l.maxLoanAmount || "",
        l.region || "",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lenders_export.csv";
    a.click();
    showNotification("Lenders exported successfully!");
  };

  return (
    <div className="space-y-4">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl border shadow-lg flex items-center gap-2 animate-slide-in ${
            notification.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {notification.type === "success" ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span className="text-xs font-medium">{notification.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-2xl bg-sky-50 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-sky-600" />
          </div>
          <div>
            <h1 className="text-sm md:text-base font-semibold text-slate-900">Lender Management</h1>
            <p className="text-[11px] text-slate-500">Create lender portal accounts and manage comprehensive lender information.</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchLenders()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
            title="Refresh list"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-sky-500 text-white text-xs font-medium hover:bg-sky-600"
          >
            <Plus className="h-4 w-4" />
            Add Lender
          </button>

          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50"
            title="Export to CSV"
          >
            <Download className="h-4 w-4" />
            Export
          </button>

          <div className="hidden md:flex items-center px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search lenders..."
              className="bg-transparent text-xs outline-none w-44"
            />
            {searchInput ? (
              <button onClick={() => setSearchInput("")} className="text-slate-400 hover:text-slate-700" title="Clear">
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 bg-white">
            <Filter className="h-4 w-4 text-slate-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent outline-none text-xs"
            >
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mobile search */}
      <div className="md:hidden bg-white rounded-2xl border border-slate-200 px-3 py-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search lenders..."
            className="bg-transparent text-xs outline-none w-full"
          />
          {searchInput ? (
            <button onClick={() => setSearchInput("")} className="text-slate-400 hover:text-slate-700" title="Clear">
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500">
            <tr>
                <th className="px-4 py-3 text-left font-medium">Lender ID</th>
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <TableSkeleton rows={8} cols={6} />
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-xs text-slate-500">
                  No lenders found.
                </td>
              </tr>
            ) : (
              filtered.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/40">
                  <td className="px-4 py-3 text-slate-900">{l.id}</td>
                  <td className="px-4 py-3 text-slate-800">{l.name}</td>
                  <td className="px-4 py-3 text-slate-800">{l.email}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={l.status} tone={l.tone} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 text-slate-700">
                      <button className="hover:text-sky-500" onClick={() => openEdit(l)} title="Edit lender">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button className="hover:text-sky-500" onClick={() => openView(l)} title="View lender">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button className="hover:text-red-500" onClick={() => deleteLender(l.id)} title="Delete lender">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <Modal
        open={modal.open}
        title={
          modal.mode === "add"
            ? "Add Lender (Create Portal Account)"
            : modal.mode === "edit"
            ? "Edit Lender"
            : "View Lender"
        }
        onClose={closeModal}
        footer={
          <div className="w-full flex items-center justify-between">
            <button
              onClick={closeModal}
              className="px-3 py-2 rounded-xl text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>

            {modal.mode === "add" ? (
              <button
                onClick={saveAdd}
                className="px-3 py-2 rounded-xl text-xs font-medium bg-sky-500 text-white hover:bg-sky-600"
              >
                Create lender
              </button>
            ) : modal.mode === "edit" ? (
              <button
                onClick={saveEdit}
                className="px-3 py-2 rounded-xl text-xs font-medium bg-sky-500 text-white hover:bg-sky-600"
              >
                Save changes
              </button>
            ) : null}
          </div>
        }
      >
        {!draft ? null : (
          <div className="space-y-4">
            {/* flash password */}
            {flashPassword ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <div className="font-semibold">Password generated</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code className="px-2 py-1 rounded-xl border border-amber-200 bg-white text-[11px]">
                    {flashPassword}
                  </code>
                  <button
                    onClick={() => navigator.clipboard?.writeText(flashPassword)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-600 text-white text-xs font-medium hover:bg-amber-700"
                    type="button"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </button>
                </div>
                <div className="mt-1 text-[11px] text-amber-700">
                  Show this once to admin only. When wired to backend, don't store password in frontend state.
                </div>
              </div>
            ) : null}

            {/* Basic Information Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-500" />
                Basic Information
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Lender ID" icon={KeyRound}>
                  <Input
                    value={draft.id}
                    onChange={(v) => setDraft((d) => ({ ...d, id: v }))}
                    placeholder="Auto-generated"
                    disabled={modal.mode !== "add"}
                  />
                </Field>

                <Field label="Status" icon={Filter}>
                  <Select
                    value={draft.status}
                    onChange={(v) => setStatus(v)}
                    disabled={modal.mode === "view"}
                    options={[
                      { label: "Active", value: "Active" },
                      { label: "Inactive", value: "Inactive" },
                    ]}
                  />
                </Field>

                <Field label="Lender Name *" icon={Building2}>
                  <Input
                    value={draft.name || ""}
                    onChange={(v) => {
                      setDraft((d) => ({
                        ...d,
                        name: v,
                        username: d.username ? d.username : slugifyUsername(v),
                      }));
                    }}
                    placeholder="e.g. Paragon Bank"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                <Field label="Email *" icon={Mail}>
                  <Input
                    type="email"
                    value={draft.email || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, email: v }))}
                    placeholder="contact@lender.com"
                    disabled={modal.mode === "view"}
                  />
                </Field>
              </div>
            </div>

            {/* Portal Credentials Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4 text-slate-500" />
                Portal Credentials
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Portal username *" icon={User}>
                  <Input
                    value={draft.username || ""}
                    onChange={(v) => setDraft((d) => ({ ...d, username: slugifyUsername(v) }))}
                    placeholder="e.g. paragon"
                    disabled={modal.mode === "view"}
                  />
                </Field>

                {modal.mode !== "view" && (
                  <div className="flex items-end">
                    <button
                      onClick={resetPassword}
                      type="button"
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <KeyRound className="h-4 w-4 text-slate-500" />
                      {modal.mode === "add" ? "Regenerate Password" : "Reset Password"}
                    </button>
                  </div>
                )}
              </div>

              {modal.mode !== "view" && draft.password && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Password (shown when generated/reset)" icon={Lock}>
                    <Input
                      value={draft.password || ""}
                      onChange={(v) => setDraft((d) => ({ ...d, password: v }))}
                      placeholder="Generated password"
                      type="text"
                      disabled={modal.mode === "view"}
                    />
                  </Field>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(draft.password || "")}
                      disabled={!draft.password}
                      className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-sky-500 text-white text-xs font-medium hover:bg-sky-600 disabled:opacity-50"
                    >
                      <Copy className="h-4 w-4" />
                      Copy password
                    </button>
                  </div>
                </div>
              )}

              {modal.mode === "view" && (
                <div className="mt-3 text-[11px] text-slate-500">Password is hidden in view mode for security.</div>
              )}
            </div>

            {/* Notes Section */}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                Additional Notes
              </div>
              <Field label="Notes" icon={FileText}>
                <Textarea
                  value={draft.notes || ""}
                  onChange={(v) => setDraft((d) => ({ ...d, notes: v }))}
                  placeholder="Add any additional notes about this lender..."
                  disabled={modal.mode === "view"}
                  rows={4}
                />
              </Field>
            </div>

            {/* hint */}
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
              <div className="font-semibold mb-1">Note:</div>
              <div>Lender will complete their profile information (company details, key contacts, etc.) after logging into their portal. Store only hashed password server-side.</div>
            </div>
          </div>
        )}
      </Modal>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}